import { Hotel, HotelRoom } from 'ts/hotel/interfaces/HotelResponse';
import { RoomType } from 'ts/hotel/classes/FilterModel';
import { FilterHotelModel } from 'ts/dynamic/classes/DynamicFilterHotel';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import { getDistanceByCoords } from 'utils/utils';

export const initHotels = (hotels: Hotel[], urlParams: URLSearchParams): Hotel[] => {
  return hotels.map((hotel, i) => {
    return initSingleHotel(hotel, urlParams, i);
  });
};

export const initSingleHotel = (hotel: Hotel, urlParams: URLSearchParams, i: number = 0): Hotel => {
  const distance = getDistanceByCoords(
    parseFloat(urlParams.get('lat') || '0'),
    parseFloat(urlParams.get('lon') || '0'),
    hotel.position.latitude,
    hotel.position.longitude
  );

  return {
    ...hotel,
    visible: true,
    distance,
    itemCode: hotel.item.code + i,
    roomClasses: hotel.roomClasses.map((room) => {
      const result = room.hotelRooms.map((hotelRoom) => {
        return {
          ...hotelRoom,
          displayName: `${room.desc ? room.desc + ' - ' : ''}${
            hotelRoom.desc ? hotelRoom.desc : ''
          } ${room.board.basis.desc ? room.board.basis.desc : ''}`,
        };
      });
      return {
        ...room,
        visible: true,
        visibleSm: true,
        hotelRooms: result,
      };
    }),
  };
};

export const getFilteredHotels = (
  newFitler: FilterHotelModel,
  hotels: Hotel[],
  choosenRoomPrice: number,
  numberOfPax: number,
  currency: string
): Hotel[] => {
  const roomTypesChecked = newFitler.roomTypes.reduce((acc, rtype) => {
    const checkedTypes = rtype.options
      .filter((option) => option.checked)
      .map((option) => option.name);
    acc[rtype.title as RoomType] = new Set(checkedTypes);
    return acc;
  }, {} as Record<string, Set<string>>);
  const skipRoomTypes = !Object.values(roomTypesChecked).some(
    (value) => value instanceof Set && value.size > 0
  );

  const newHotels = hotels?.map((hotel) => {
    const filteredByName = newFitler.name
      .split(' ')
      .every((name) => hotel.item.name.toLowerCase().includes(name.toLowerCase()));
    const filteredByRooms =
      newFitler.room.split(' ').every((room: string) => {
        return hotel.roomClasses.some((roomClass) => {
          return roomClass.desc?.toLowerCase().includes(room.toLowerCase());
        });
      }) || !newFitler.room;

    const skipStars = newFitler.stars.value.length === 0;
    const filteredByStars =
      skipStars ||
      newFitler.stars.value.some((star) => {
        return Math.floor(hotel.item.star) === star;
      });

    const skipAdditional = newFitler.additional.value.length === 0;
    const filteredByAdditional =
      skipAdditional ||
      newFitler.additional.value.some((item) => {
        return hotel.roomClasses.some((roomClass) => {
          return roomClass.board.basis.code === item;
        });
      });
    const filteredByCxl =
      !newFitler.additional.freeCxl || (newFitler.additional.freeCxl && hotel.freeCxl);

    const roomClasses = hotel.roomClasses.map((room) => {
      const filteredByName =
        newFitler.room
          .split(' ')
          .every((name) => room.desc?.toLocaleLowerCase().includes(name.toLocaleLowerCase())) ||
        !newFitler.room;
      const filteredByMeal =
        !newFitler.additional.withMeal ||
        (newFitler.additional.withMeal && room.board.basis.code !== 'RO');

      const skipAdditional = newFitler.additional.value.length === 0;
      const filteredByAdditional =
        skipAdditional ||
        newFitler.additional.value.some((item) => {
          return room.board.basis.code === item;
        });

      const filteredByCxl =
        !newFitler.additional.freeCxl ||
        (newFitler.additional.freeCxl && new Date(room.cxl.limitDate) > new Date());
      const priceValue = Math.round(
        room.price[currency as keyof Price] / numberOfPax - choosenRoomPrice
      );
      const minValue = newFitler.price.value[0];
      const maxValue = newFitler.price.value[1];

      const filteredByPrice = priceValue >= minValue && priceValue <= maxValue;

      const filteredByRoomTypes =
        skipRoomTypes ||
        newFitler.roomTypes.every((rtype) => {
          const mode = rtype.title as RoomType;
          const checkedTypes = roomTypesChecked[mode] || new Set<string>();
          const modeKey = mode as keyof HotelRoom;
          const roomTypesInRoom = room.hotelRooms
            .filter((hRoom) => hRoom[modeKey])
            .flatMap((hRoom) => hRoom[modeKey].split(', '));

          return !checkedTypes.size || roomTypesInRoom.some((type) => checkedTypes.has(type));
        });

      return {
        ...room,
        visible:
          filteredByName &&
          filteredByAdditional &&
          filteredByCxl &&
          filteredByPrice &&
          filteredByRoomTypes &&
          filteredByMeal,
      };
    });

    const visible =
      filteredByName &&
      filteredByRooms &&
      filteredByStars &&
      filteredByCxl &&
      filteredByAdditional &&
      roomClasses.some((room) => room.visible);

    return {
      ...hotel,
      visible,
      roomClasses,
    };
  });

  return newHotels || [];
};

export const getSortedHotels = (hotels: Hotel[] | undefined, sort: string) => {
  let newHotels: Hotel[] = [];

  if (hotels?.length) {
    if (sort === 'priceIncrease') {
      newHotels = [...hotels].sort(
        (a, b) => a.roomClasses[0].price.usd - b.roomClasses[0].price.usd
      );
    } else if (sort === 'priceDecrease') {
      newHotels = [...hotels].sort(
        (a, b) => b.roomClasses[0].price.usd - a.roomClasses[0].price.usd
      );
    } else if (sort === 'starIncrease') {
      newHotels = [...hotels].sort((a, b) => a.item.star - b.item.star);
    } else if (sort === 'starDecrease') {
      newHotels = [...hotels].sort((a, b) => b.item.star - a.item.star);
    } else if (sort === 'nameIncrease') {
      newHotels = [...hotels].sort((a, b) => a.item.name.localeCompare(b.item.name));
    } else if (sort === 'nameDecrease') {
      newHotels = [...hotels].sort((a, b) => b.item.name.localeCompare(a.item.name));
    } else {
      newHotels = [...hotels];
    }
  }
  return newHotels;
};
